import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';

const DEMO_MESSAGES = [
  { from: 'client', text: 'Hola, necesito precio de 20 bultos de cemento y 10 varillas 3/8', delay: 0 },
  { from: 'bot', text: '...', typing: true, delay: 800 },
  { from: 'bot', text: '✅ 2 producto(s) cotizados.\nCotización:\n\n• 20 x Cemento Gris Monterrey 50kg — $4,200\n• 10 x Varilla corrugada 3/8" — $1,850\n\nTotal: $6,050 (IVA incluido)\n📋 Folio: CX-7KM2PN\n💳 Escribe *pagar* y te mandamos datos bancarios o link para pago con tarjeta.', delay: 2400 },
  { from: 'client', text: 'Agrega 5 sacos de pegazulejo', delay: 5500 },
  { from: 'bot', text: '...', typing: true, delay: 6300 },
  { from: 'bot', text: '✅ 3 producto(s) cotizados.\nCotización:\n\n• 20 x Cemento Gris Monterrey 50kg — $4,200\n• 10 x Varilla corrugada 3/8" — $1,850\n• 5 x Pegazulejo Interceramic 20kg — $475\n\nTotal: $6,525 (IVA incluido)\n📋 Folio: CX-7KM2PN\n💳 Escribe *pagar* y te mandamos datos bancarios o link para pago con tarjeta.', delay: 8200 },
];

function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [hasPlayed, setHasPlayed] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (open && !hasPlayed) {
      setHasPlayed(true);
      setMessages([]);
      DEMO_MESSAGES.forEach((msg, i) => {
        setTimeout(() => {
          setMessages(prev => {
            // Replace typing indicator from same sender
            if (!msg.typing) {
              const filtered = prev.filter(m => !(m.typing && m.from === msg.from));
              return [...filtered, msg];
            }
            return [...prev, msg];
          });
        }, msg.delay);
      });
    }
  }, [open, hasPlayed]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const replay = () => {
    setMessages([]);
    setHasPlayed(false);
    setTimeout(() => setHasPlayed(false), 50);
    // Trigger replay
    setTimeout(() => {
      setMessages([]);
      DEMO_MESSAGES.forEach((msg, i) => {
        setTimeout(() => {
          setMessages(prev => {
            if (!msg.typing) {
              const filtered = prev.filter(m => !(m.typing && m.from === msg.from));
              return [...filtered, msg];
            }
            return [...prev, msg];
          });
        }, msg.delay);
      });
    }, 100);
  };

  return (
    <>
      {/* Chat Dialog */}
      {open && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-50 w-[340px] sm:w-[380px] rounded-2xl shadow-2xl overflow-hidden border border-slate-200 bg-white"
             style={{ animation: 'slide-up 0.3s ease-out' }}>
          {/* Header */}
          <div className="bg-[#075E54] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">CB</div>
              <div>
                <p className="font-semibold text-sm leading-tight">CotizaBot Demo</p>
                <p className="text-[11px] text-emerald-200">En linea</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white text-xl leading-none p-1">&times;</button>
          </div>
          {/* Messages */}
          <div className="h-[320px] overflow-y-auto px-3 py-3 space-y-2"
               style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'200\' height=\'200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'p\' width=\'40\' height=\'40\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M0 20h40M20 0v40\' stroke=\'%23e8e5de\' stroke-width=\'.5\' fill=\'none\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'200\' height=\'200\' fill=\'%23ECE5DD\'/%3E%3Crect width=\'200\' height=\'200\' fill=\'url(%23p)\'/%3E%3C/svg%3E")' }}>
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.from === 'client' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-lg text-[13px] leading-relaxed shadow-sm whitespace-pre-line ${
                  msg.from === 'client'
                    ? 'bg-[#DCF8C6] text-slate-800 rounded-tr-none'
                    : 'bg-white text-slate-800 rounded-tl-none'
                }`}>
                  {msg.typing ? (
                    <span className="flex gap-1 py-1 px-1">
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </span>
                  ) : (
                    <span dangerouslySetInnerHTML={{ __html: msg.text.replace(/\*([^*]+)\*/g, '<b>$1</b>').replace(/\n/g, '<br/>') }} />
                  )}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          {/* Footer CTA */}
          <div className="border-t border-slate-200 px-3 py-3 bg-white flex flex-col gap-2">
            <a
              href="https://wa.me/5218342472640?text=DEMO"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-[#25D366] hover:bg-[#1ebe57] text-white text-center py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Pruebalo en WhatsApp ahora
            </a>
            <button onClick={replay} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
              Repetir demo
            </button>
          </div>
        </div>
      )}

      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`fixed bottom-6 right-4 sm:right-6 z-50 flex items-center gap-2 text-white pl-4 pr-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 ${
          open ? 'bg-slate-600 hover:bg-slate-700' : 'bg-[#25D366] hover:bg-[#1ebe57]'
        }`}
        style={!open ? { animation: 'float-pulse 3s ease-in-out infinite' } : {}}
      >
        {open ? (
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
            <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/>
          </svg>
        )}
        <span className="font-semibold text-sm whitespace-nowrap">
          {open ? 'Cerrar' : 'Prueba el bot'}
        </span>
      </button>

      <style>{`
        @keyframes float-pulse {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}

export default function LandingPage() {
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setSlide((p) => (p + 1) % 2), 7000);
    return () => clearInterval(t);
  }, []);
  const [installEvt, setInstallEvt] = useState(null);
  const [installHelp, setInstallHelp] = useState(false);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);
  // Detección de visitantes en USA (por zona horaria, sin APIs ni permisos)
  const [showUsaBanner, setShowUsaBanner] = useState(false);
  useEffect(() => {
    try {
      if (localStorage.getItem('cotizador_region')) return; // ya eligió
      if (sessionStorage.getItem('usa_banner_off')) return;
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const US_TZ = ['America/New_York','America/Chicago','America/Denver','America/Phoenix','America/Los_Angeles','America/Anchorage','America/Detroit','America/Indiana/Indianapolis','America/Boise','Pacific/Honolulu','America/Kentucky/Louisville'];
      if (US_TZ.includes(tz)) setShowUsaBanner(true);
    } catch (e) { /* noop */ }
  }, []);
  const irUSA = () => { try { localStorage.setItem('cotizador_region', 'US'); } catch (e) {} window.location.href = '/usa'; };
  const cerrarUsaBanner = () => { try { sessionStorage.setItem('usa_banner_off', '1'); } catch (e) {} setShowUsaBanner(false); };
  return (

    <div className="min-h-screen" data-testid="landing-page">
      {showUsaBanner && (
        <div className="bg-blue-800 text-white text-sm px-4 py-2.5 flex items-center justify-center gap-3 relative z-[60]">
          <span>🇺🇸 ¿Trabajas en Estados Unidos? Tenemos estimates en español, en dólares y con tax.</span>
          <button onClick={irUSA} className="bg-yellow-400 text-slate-900 font-bold rounded-full px-4 py-1 hover:bg-yellow-300 whitespace-nowrap">Ver precios en USD →</button>
          <button onClick={cerrarUsaBanner} aria-label="Cerrar" className="text-blue-200 hover:text-white font-bold px-1">✕</button>
        </div>
      )}
      <Helmet>
        <title>Cotiza por WhatsApp en Automático — Bot que Responde en 5 Segundos | CotizaExpress</title>
        <meta name="description" content="Tu cliente manda su lista por WhatsApp y recibe su cotización con IVA y PDF al instante, 24/7. Sube tu Excel, conecta tu número y pruébalo gratis hoy — sin tarjeta." />
        <link rel="canonical" href="https://cotizaexpress.com/" />
        <meta property="og:title" content="CotizaBot - Tu negocio cotiza solo por WhatsApp" />
        <meta property="og:description" content="Tus clientes preguntan precio por WhatsApp y reciben cotizacion con IVA en 3 segundos. Sin empleado. Sin errores. 24/7." />
        <meta property="og:url" content="https://cotizaexpress.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://cotizaexpress.com/og-cotizabot.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="es_MX" />
        <meta property="og:site_name" content="CotizaExpress" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CotizaBot - Cotizaciones automaticas por WhatsApp" />
        <meta name="twitter:description" content="Tu negocio cotiza solo. Respuestas en 3 segundos con IA por WhatsApp." />
        <meta name="twitter:image" content="https://cotizaexpress.com/og-cotizabot.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CotizaBot",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web, WhatsApp",
            "description": "Sistema de IA que automatiza cotizaciones por WhatsApp para negocios con catálogo de productos en México",
            "url": "https://cotizaexpress.com",
            "offers": [
              {
                "@type": "Offer",
                "name": "CotizaBot",
                "price": "1000",
                "priceCurrency": "MXN",
                "priceValidUntil": "2027-12-31",
                "availability": "https://schema.org/InStock"
              },
              {
                "@type": "Offer",
                "name": "CotizaBot Pro",
                "price": "2000",
                "priceCurrency": "MXN",
                "priceValidUntil": "2027-12-31",
                "availability": "https://schema.org/InStock"
              }
            ],
            "provider": {
              "@type": "Organization",
              "name": "CotizaExpress",
              "url": "https://cotizaexpress.com",
              "logo": "https://cotizaexpress.com/logo-cotizabot.png",
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "sales",
                "availableLanguage": "Spanish"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "12"
            }
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Como funciona CotizaBot para mi negocio?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Subes tu catalogo de productos, compartes tu link de WhatsApp con tus clientes, y CotizaBot responde automaticamente con precios y cotizaciones con IVA en 3 segundos."
                }
              },
              {
                "@type": "Question",
                "name": "Necesito verificar mi numero con Meta o WhatsApp?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. CotizaBot usa un numero centralizado ya verificado. Solo compartes tu codigo unico con tus clientes."
                }
              },
              {
                "@type": "Question",
                "name": "Puedo cobrar a mis clientes por WhatsApp?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Si, con el CotizaBot Pro puedes enviar links de pago de Mercado Pago o datos de transferencia SPEI automaticamente."
                }
              },
              {
                "@type": "Question",
                "name": "Cuanto cuesta CotizaBot?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "El CotizaBot cuesta $1,000 MXN/mes + IVA con cotizaciones ilimitadas y WhatsApp integrado. El CotizaBot Pro cuesta $2,000 MXN/mes + IVA e incluye cobros automaticos."
                }
              }
            ]
          }
        `}</script>
      </Helmet>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-2xl shadow-md shadow-emerald-200 flex-shrink-0">🤖</div>
              <div className="hidden sm:block">
                <span className="text-lg font-extrabold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">CotizaBot</span>
                <span className="text-xs text-slate-500 block -mt-1">by CotizaExpress.com</span>
              </div>
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/precios" className="text-slate-600 hover:text-emerald-600 font-medium">Precios</Link>
              <Link to="/login">
                <Button variant="ghost" className="text-slate-600">Entrar</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Botón flotante móvil: descargar app */}
      <button
        onClick={() => { if (installEvt) { installEvt.prompt(); setInstallEvt(null); } else { setInstallHelp(true); } }}
        className="sm:hidden fixed bottom-5 left-4 z-40 bg-slate-900 text-white font-bold text-sm rounded-full px-4 py-3 shadow-2xl flex items-center gap-2"
      >
        📲 Descargar app
      </button>

      {installHelp && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={() => setInstallHelp(false)}>
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-3xl mx-auto mb-2">🤖</div>
              <p className="font-bold text-lg text-slate-900">Agrega CotizaBot a tu pantalla</p>
              <p className="text-sm text-slate-500">Queda como app, junto a tu WhatsApp. Gratis.</p>
            </div>
            <div className="space-y-4 text-sm">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-bold text-slate-900 mb-1">🍎 iPhone (Safari)</p>
                <p className="text-slate-600">1. Toca el botón <strong>compartir</strong> ↑ (abajo en medio)<br/>2. Toca <strong>"Agregar a pantalla de inicio"</strong><br/>3. Toca <strong>Agregar</strong> — ¡listo!</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-bold text-slate-900 mb-1">🤖 Android (Chrome)</p>
                <p className="text-slate-600">1. Toca los <strong>3 puntitos</strong> ⋮ (arriba a la derecha)<br/>2. Toca <strong>"Agregar a pantalla principal"</strong><br/>3. Confirma — ¡listo!</p>
              </div>
            </div>
            <button onClick={() => setInstallHelp(false)} className="mt-4 w-full bg-slate-900 text-white font-bold rounded-full py-3">Entendido</button>
          </div>
        </div>
      )}

      {/* Hero Section — carrusel de 2 productos */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-teal-50 py-10 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">

            {/* Texto del slide */}
            <div className="text-center lg:text-left order-1">
              {slide === 0 ? (
                <>
                  <p className="text-sm font-bold text-emerald-700 mb-2">🤖 COTIZA AUTOMÁTICO</p>
                  <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-5 leading-tight">
                    Tu cliente pregunta precio.<br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">Tu WhatsApp cotiza solo.</span>
                  </h1>
                  <p className="text-lg text-slate-600 mb-7 max-w-xl mx-auto lg:mx-0">
                    El bot lee la lista que te mandan, busca en tu catálogo y responde la cotización con IVA y PDF en 5 segundos — 24/7.
                  </p>
                  <a href="https://wa.me/5218342472640?text=DEMO" target="_blank" rel="noopener noreferrer" onClick={() => { if (window.fbq) { try { window.fbq('track', 'Lead'); } catch (e) {} } }}>
                    <Button size="lg" className="text-lg px-8 py-6 bg-green-600 hover:bg-green-700 shadow-xl">Pruébalo en tu WhatsApp →</Button>
                  </a>
                  <p className="text-sm text-slate-400 mt-3">Gratis · sin registro · es un chat, nada que instalar</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-bold text-violet-700 mb-2">✨ HAZ TUS COTIZACIONES</p>
                  <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-5 leading-tight">
                    Pega la lista de tu cliente.<br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600">La IA te la cotiza.</span>
                  </h1>
                  <p className="text-lg text-slate-600 mb-7 max-w-xl mx-auto lg:mx-0">
                    Cotización con IVA, folio y PDF en segundos — y antes de mandarla, <strong className="text-violet-700">revisa cuánto te queda</strong>. Desde $299/mes.
                  </p>
                  <Link to="/registro">
                    <Button size="lg" className="text-lg px-8 py-6 bg-violet-600 hover:bg-violet-700 shadow-xl">Empieza gratis →</Button>
                  </Link>
                  <p className="text-sm text-slate-400 mt-3">Sin tarjeta · se instala como app en tu celular 📲</p>
                </>
              )}
            </div>

            {/* "Imagen" del slide (mockup) */}
            <div className="order-2">
              {slide === 0 ? (
                <div className="max-w-[300px] sm:max-w-sm mx-auto bg-[#ECE5DD] rounded-3xl p-3 sm:p-4 shadow-2xl border border-slate-200">
                  <div className="bg-[#075E54] text-white rounded-t-2xl px-4 py-2 text-sm font-semibold flex items-center gap-2">🤖 Ferretería La Esperanza</div>
                  <div className="space-y-2 p-3">
                    <div className="bg-white rounded-2xl rounded-tl-none px-3 py-2 text-sm shadow-sm max-w-[85%]">me das precio<br/>10 cemento<br/>5 varilla 3/8<br/>200 block</div>
                    <div className="bg-[#DCF8C6] rounded-2xl rounded-tr-none px-3 py-2 text-sm shadow-sm ml-auto max-w-[90%]">
                      ✅ <strong>Cotización CX-00457</strong><br/>
                      10 Cemento gris 50kg — $2,450<br/>
                      5 Varilla 3/8 — $840<br/>
                      200 Block 15x20 — $3,700<br/>
                      <strong>TOTAL (IVA inc.): $8,108.40</strong><br/>
                      📄 PDF adjunto
                    </div>
                    <p className="text-center text-[11px] text-slate-500">⚡ respondió en 5 segundos — 11:47 PM</p>
                  </div>
                </div>
              ) : (
                <div className="max-w-[300px] sm:max-w-sm mx-auto bg-white rounded-3xl p-4 sm:p-5 shadow-2xl border border-slate-200">
                  <p className="text-xs font-bold text-violet-700 mb-2">✨ Cotizador IA</p>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-500 mb-3">10 cemento, 5 varilla 3/8, 200 block…</div>
                  <div className="text-center text-violet-500 text-xl mb-3">⬇</div>
                  <div className="border border-slate-200 rounded-xl overflow-hidden text-xs">
                    <div className="grid grid-cols-3 bg-slate-100 px-3 py-1.5 font-semibold text-slate-600"><span>Producto</span><span className="text-center">Cant.</span><span className="text-right">Importe</span></div>
                    <div className="grid grid-cols-3 px-3 py-1.5 border-t"><span>Cemento gris 50kg</span><span className="text-center">10</span><span className="text-right">$2,450</span></div>
                    <div className="grid grid-cols-3 px-3 py-1.5 border-t bg-slate-50"><span>Varilla 3/8</span><span className="text-center">5</span><span className="text-right">$840</span></div>
                    <div className="grid grid-cols-3 px-3 py-1.5 border-t"><span>Block 15x20</span><span className="text-center">200</span><span className="text-right">$3,700</span></div>
                    <div className="flex justify-between px-3 py-2 border-t bg-violet-600 text-white font-bold"><span>TOTAL (IVA inc.)</span><span>$8,108.40</span></div>
                  </div>
                  <div className="mt-3 bg-violet-50 border border-violet-200 rounded-xl px-3 py-2 text-xs">
                    <p className="text-violet-500 font-semibold mb-1">🔒 Solo tú lo ves (no sale en el PDF)</p>
                    <div className="flex justify-between text-violet-700"><span>Venta sin IVA · te cuesta</span><span>$6,990 · $5,250</span></div>
                    <div className="flex justify-between font-bold text-emerald-700"><span>💰 Ganancia estimada</span><span>$1,740 (24.9%)</span></div>
                  </div>
                  <p className="text-center text-xs mt-3 font-semibold">📄 <a href="/ejemplos/cotizacion-ejemplo.pdf" target="_blank" rel="noopener noreferrer" className="text-violet-700 underline">Abre una cotización real de ejemplo (PDF)</a></p>
                </div>
              )}
            </div>
          </div>

          {/* Controles del carrusel */}
          <div className="flex justify-center gap-3 mt-10">
            <button onClick={() => setSlide(0)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${slide === 0 ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-300 text-slate-600'}`}>🤖 Cotiza automático</button>
            <button onClick={() => setSlide(1)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${slide === 1 ? 'bg-violet-600 text-white' : 'bg-white border border-slate-300 text-slate-600'}`}>✨ Haz tus cotizaciones</button>
          </div>
        </div>
      </section>

      {/* Franja de confianza (una línea) */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-slate-600">
          <span>✅ IVA automático</span>
          <span>⚡ Responde en 3 segundos, 24/7</span>
          <span>📲 Vive en tu celular, como app</span>
          <span>🇲🇽 Soporte en español</span>
        </div>
      </section>

      {/* Precios resumen */}
      <section className="py-16 bg-white" id="planes">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-slate-900 mb-10">Planes y Precios</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 border-violet-300 shadow-lg">
              <CardContent className="pt-8 pb-8 text-center">
                <p className="text-3xl mb-2">✨</p>
                <h3 className="text-xl font-bold mb-1">Cotizador IA</h3>
                <p className="text-sm text-slate-500 mb-4 min-h-[40px]">Haz tus cotizaciones en 2 minutos — sin conectar WhatsApp</p>
                <div className="text-4xl font-bold text-violet-600 mb-1">$299</div>
                <p className="text-slate-500 text-sm mb-6">MXN/mes · la primera gratis</p>
                <Link to="/registro"><Button className="w-full bg-violet-600 hover:bg-violet-700" size="lg">Empezar gratis</Button></Link>
              </CardContent>
            </Card>
            <Card className="border-2 border-amber-400 shadow-xl relative overflow-hidden">
              <div className="bg-amber-400 text-white text-center py-1.5 text-xs font-bold tracking-wide">RECOMENDADO</div>
              <CardContent className="pt-6 pb-8 text-center">
                <p className="text-3xl mb-2">🤖</p>
                <h3 className="text-xl font-bold mb-1">CotizaBot</h3>
                <p className="text-sm text-slate-500 mb-4 min-h-[40px]">Tu WhatsApp contesta y cotiza solo, 24/7</p>
                <div className="text-4xl font-bold text-slate-800 mb-1">$1,000</div>
                <p className="text-slate-500 text-sm mb-6">MXN/mes</p>
                <Link to="/registro?intent=bot"><Button className="w-full bg-amber-500 hover:bg-amber-600 text-white" size="lg">Empezar ahora</Button></Link>
              </CardContent>
            </Card>
            <Card className="border-2 border-orange-300 shadow-lg">
              <CardContent className="pt-8 pb-8 text-center">
                <p className="text-3xl mb-2">💰</p>
                <h3 className="text-xl font-bold mb-1">CotizaBot Pro</h3>
                <p className="text-sm text-slate-500 mb-4 min-h-[40px]">Además cobra: links de pago y SPEI por WhatsApp</p>
                <div className="text-4xl font-bold text-orange-500 mb-1">$2,000</div>
                <p className="text-slate-500 text-sm mb-6">MXN/mes</p>
                <Link to="/registro?intent=bot"><Button className="w-full bg-orange-500 hover:bg-orange-600 text-white" size="lg">Quiero cobrar</Button></Link>
              </CardContent>
            </Card>
          </div>
          <p className="text-center text-slate-500 mt-6 text-sm"><Link to="/precios" className="text-emerald-600 hover:underline">Ver comparativa completa →</Link></p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 bg-slate-50" id="faq">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-xl font-bold text-center text-slate-900 mb-5">Preguntas frecuentes</h2>
          <div className="space-y-2">
            {[
              ['¿Sirve mi número de WhatsApp de siempre?', 'Sí. Conectas tu número actual sin perder tu WhatsApp — o usas otro si prefieres.'],
              ['¿Cómo subo mis productos?', 'Con tu Excel en 1 clic, o a mano desde el panel.'],
              ['¿Calcula el IVA?', 'Sí, automático y configurable (incluido en tus precios o agregado al total).'],
              ['¿Puedo cobrar por WhatsApp?', 'Sí, con CotizaBot Pro: links de Mercado Pago y datos SPEI automáticos.'],
              ['¿Puedo probarlo antes?', 'Sí: cualquier plan se prueba 7 días gratis y sin tarjeta. Al terminar se corta y tú decides si pagas. También puedes probar el bot en el simulador sin conectar nada.'],
            ].map(([q, a2]) => (
              <details key={q} className="bg-white border border-slate-200 rounded-lg px-4 py-3">
                <summary className="text-sm font-semibold text-slate-800 cursor-pointer">{q}</summary>
                <p className="text-sm text-slate-600 mt-2">{a2}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-2xl shadow-md shadow-emerald-200 flex-shrink-0">🤖</div>
                <div>
                  <span className="text-lg font-bold text-white">CotizaBot</span>
                  <span className="text-xs text-slate-400 block">by CotizaExpress.com</span>
                </div>
              </Link>
              <p className="text-slate-400 text-sm max-w-md">Sistema de IA para automatizar cotizaciones y ventas por WhatsApp. Diseñado para PYMEs mexicanas.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Producto</h4>
              <ul className="space-y-1 text-xs">
                <li><Link to="/precios" className="text-slate-400 hover:text-white">Precios</Link></li>
                <li><Link to="/demo" className="text-slate-400 hover:text-white">Demo</Link></li>
                <li><Link to="/blog" className="text-slate-400 hover:text-white">Blog</Link></li>
                <li><Link to="/generador-de-cotizaciones" className="text-slate-400 hover:text-white">Generador de Cotizaciones Gratis</Link></li>
                <li><Link to="/cotizacion-formal" className="text-slate-400 hover:text-white">Cotización formal: ejemplo</Link></li>
                <li><Link to="/cotizador-para-contratistas" className="text-slate-400 hover:text-white">Cotizador para contratistas</Link></li>
                <li><Link to="/mensajes-para-clientes-whatsapp" className="text-slate-400 hover:text-white">Mensajes para clientes por WhatsApp</Link></li>
                <li><Link to="/mensajes-para-recuperar-clientes-whatsapp" className="text-slate-400 hover:text-white">Mensajes para recuperar clientes</Link></li>
                <li><Link to="/cuanto-cobrar-por-pintar" className="text-slate-400 hover:text-white">¿Cuánto cobrar por pintar?</Link></li>
                <li><Link to="/cuanto-cobrar-por-drywall" className="text-slate-400 hover:text-white">¿Cuánto cobrar por drywall?</Link></li>
                <li><Link to="/cuanto-cobrar-por-instalar-piso" className="text-slate-400 hover:text-white">¿Cuánto cobrar por instalar piso?</Link></li>
                <li><Link to="/cuanto-cobrar-por-un-techo" className="text-slate-400 hover:text-white">¿Cuánto cobrar por un techo?</Link></li>
                <li><Link to="/calculadora-iva" className="text-slate-400 hover:text-white">Calculadora de IVA</Link></li>
                <li><Link to="/plantillas/cotizacion" className="text-slate-400 hover:text-white">Plantilla de Cotización</Link></li>
                <li><Link to="/calculadoras" className="text-slate-400 hover:text-white">Calculadoras</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Soluciones por giro</h4>
              <ul className="space-y-1 text-xs">
                <li><Link to="/ferreterias" className="text-slate-400 hover:text-white">Ferreterías</Link></li>
                <li><Link to="/materiales-construccion" className="text-slate-400 hover:text-white">Materiales de Construcción</Link></li>
                <li><Link to="/tablaroca-plafones" className="text-slate-400 hover:text-white">Tablaroca y Plafones</Link></li>
                <li><Link to="/cancelerias" className="text-slate-400 hover:text-white">Cancelerías</Link></li>
                <li><Link to="/imprentas" className="text-slate-400 hover:text-white">Imprentas y Rotulación</Link></li>
                <li><Link to="/climas" className="text-slate-400 hover:text-white">Climas y Refrigeración</Link></li>
                <li><Link to="/refaccionarias" className="text-slate-400 hover:text-white">Refaccionarias</Link></li>
                <li><Link to="/plomerias" className="text-slate-400 hover:text-white">Plomerías</Link></li>
                <li><Link to="/material-electrico" className="text-slate-400 hover:text-white">Material Eléctrico</Link></li>
                <li><Link to="/pinturas" className="text-slate-400 hover:text-white">Pinturas</Link></li>
                <li><Link to="/herrerias" className="text-slate-400 hover:text-white">Herrerías</Link></li>
                <li><Link to="/distribuidoras" className="text-slate-400 hover:text-white">Distribuidoras</Link></li>
                <li><Link to="/papelerias" className="text-slate-400 hover:text-white">Papelerías</Link></li>
                <li><Link to="/abarrotes-mayoreo" className="text-slate-400 hover:text-white">Abarrotes Mayoreo</Link></li>
                <li><Link to="/muebleria" className="text-slate-400 hover:text-white">Mueblerías</Link></li>
                <li><Link to="/servicios-tecnicos" className="text-slate-400 hover:text-white">Servicios Técnicos</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-3 text-sm">Contacto</h4>
              <ul className="space-y-1 text-xs">
                <li><a href="https://wa.me/5218342472640?text=Hola%2C%20me%20interesa%20CotizaBot" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.611.611l4.458-1.495A11.96 11.96 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.319 0-4.476-.693-6.283-1.882l-.438-.29-2.65.889.889-2.65-.29-.438A9.953 9.953 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>
                  WhatsApp Ventas
                </a></li>
                <li><a href="mailto:contacto@arobegroup.com" className="text-slate-400 hover:text-white flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  contacto@arobegroup.com
                </a></li>
                <li><Link to="/contacto" className="text-slate-400 hover:text-white">Página de Contacto</Link></li>
              </ul>
              <h4 className="text-white font-semibold mb-4 mt-6">Legal</h4>
              <ul className="space-y-1 text-xs">
                <li><Link to="/privacidad" className="text-slate-400 hover:text-white">Aviso de Privacidad</Link></li>
                <li><Link to="/terminos" className="text-slate-400 hover:text-white">Términos y Condiciones</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-400 text-sm">© 2026 CotizaExpress.com - CotizaBot. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
      <ChatWidget />
    </div>
  );
}
